import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import ArticlesConnexes from '@/components/ArticlesConnexes';
import NewsletterCapture from '@/components/NewsletterCapture';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, TrendingUp, DollarSign, Users, Star, CheckCircle2, AlertCircle, Clock3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ConciergeriePourquoiDeleguer() {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Conciergerie Airbnb : Pourquoi Déléguer Votre Gestion en 2025",
    "description": "Analyse complète ROI conciergerie Airbnb : gain temps 15h/semaine, revenus +28%, erreurs évitées. Comparatif gestion perso vs pro et calcul rentabilité réelle.",
    "image": "https://images.unsplash.com/photo-1562770584-eaf50b017307?q=80&w=1200",
    "datePublished": "2025-01-12",
    "dateModified": "2025-01-12",
    "author": {
      "@type": "Organization",
      "name": "Homely Conciergerie"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Homely Conciergerie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://homely-conciergerie.ch/logo-main.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://homely-conciergerie.ch/blog/conciergerie-airbnb-pourquoi-deleguer"
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Conciergerie Airbnb : Pourquoi Déléguer en 2025 ? ROI & Analyse | Homely"
        description="Analyse ROI conciergerie Airbnb : +28% revenus, 15h/semaine économisées, 0 stress. Comparatif coûts gestion perso vs pro. Guide complet 2025."
        keywords="conciergerie airbnb, gestion airbnb déléguée, cout conciergerie suisse, roi gestion locative, airbnb management, pourquoi déléguer"
        canonical="https://homely-conciergerie.ch/blog/conciergerie-airbnb-pourquoi-deleguer"
        image="https://images.unsplash.com/photo-1562770584-eaf50b017307?q=80&w=1200"
        type="article"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.75), rgba(43, 43, 43, 0.85)), url('https://images.unsplash.com/photo-1562770584-eaf50b017307?q=80&w=2000')`,
          }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 text-white/80 mb-6">
              <span className="bg-[#8A9474] px-4 py-1 rounded-full text-sm font-semibold">Stratégie & Rentabilité</span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">12 janvier 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span className="text-sm">12 min de lecture</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Conciergerie Airbnb : Pourquoi Déléguer Votre Gestion en 2025 ?
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed">
              Vous gérez votre Airbnb seul et cela vous prend <strong>15h/semaine</strong> ? Une conciergerie professionnelle génère <strong>+28% de revenus</strong> tout en vous libérant totalement. Analyse ROI complète et calcul de rentabilité réelle.
            </p>
          </div>
        </section>

        <article className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-lg text-[#4A4A4A] leading-relaxed">
                La gestion autonome d'un Airbnb semble économique au premier abord. Pourtant, entre le temps investi, les erreurs coûteuses et les revenus non optimisés, <strong>85% des propriétaires sous-performent</strong> face aux professionnels. Voici pourquoi déléguer est souvent plus rentable.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <Card className="border-2 border-[#8A9474]">
                <CardContent className="p-6 text-center">
                  <Clock3 className="h-12 w-12 text-[#8A9474] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-[#2B2B2B] mb-2">15h/semaine</div>
                  <div className="text-sm text-[#4A4A4A]">Temps économisé en déléguant</div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#8A9474]">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-12 w-12 text-[#8A9474] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-[#2B2B2B] mb-2">+28%</div>
                  <div className="text-sm text-[#4A4A4A]">Revenus moyens supplémentaires (pro)</div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#8A9474]">
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-12 w-12 text-[#8A9474] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-[#2B2B2B] mb-2">22%</div>
                  <div className="text-sm text-[#4A4A4A]">Commission moyenne conciergerie pro</div>
                </CardContent>
              </Card>
            </div>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6 flex items-center gap-3">
                <Clock3 className="h-8 w-8 text-[#8A9474]" />
                1. Charge Mentale & Temps Réel : Le Coût Caché
              </h2>

              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h3 className="text-xl font-bold text-[#2B2B2B] mb-4">Temps Réel Investi par Semaine (Gestion Solo)</h3>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-[#8A9474] pl-4">
                    <h4 className="font-bold text-[#2B2B2B] mb-2">📧 Communication Voyageurs (4-6h)</h4>
                    <ul className="list-disc list-inside text-[#4A4A4A] space-y-1">
                      <li>Réponses questions pré-réservation (délai &lt;24h critique)</li>
                      <li>Instructions check-in personnalisées</li>
                      <li>Gestion demandes pendant séjour</li>
                      <li>Suivi avis post-départ</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-[#8A9474] pl-4">
                    <h4 className="font-bold text-[#2B2B2B] mb-2">🧹 Coordination Ménage & Logistique (3-5h)</h4>
                    <ul className="list-disc list-inside text-[#4A4A4A] space-y-1">
                      <li>Organisation ménages entre 2 séjours (timing serré)</li>
                      <li>Vérification qualité après chaque passage</li>
                      <li>Gestion stocks (linge, produits, consommables)</li>
                      <li>Coordination réparations urgentes</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-[#8A9474] pl-4">
                    <h4 className="font-bold text-[#2B2B2B] mb-2">📊 Optimisation Annonce & Pricing (2-4h)</h4>
                    <ul className="list-disc list-inside text-[#4A4A4A] space-y-1">
                      <li>Ajustements tarifaires quotidiens (concurrence, météo, événements)</li>
                      <li>Mise à jour photos saisonnières</li>
                      <li>A/B testing descriptions</li>
                      <li>Veille réglementation locale</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-gray-300 pl-4">
                    <h4 className="font-bold text-[#2B2B2B] mb-2">🆘 Gestion Crises & Imprévus (2-3h)</h4>
                    <ul className="list-disc list-inside text-[#4A4A4A] space-y-1">
                      <li>Annulations last-minute (re-commercialisation urgente)</li>
                      <li>Problèmes techniques (chauffage, WiFi, serrure)</li>
                      <li>Litiges voyageurs (dégâts, plaintes voisins)</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-[#E8EBE4] rounded-lg">
                  <p className="text-[#2B2B2B] font-semibold mb-2">💡 Calcul Coût Opportunité</p>
                  <p className="text-sm text-[#4A4A4A]">
                    15h/semaine × 50 semaines = <strong>750h/an</strong>. Au taux horaire professionnel de 80 CHF, c'est <strong>60'000 CHF</strong> de valeur-temps non rémunérée. Une conciergerie à 22% sur 80K CHF revenus = <strong>17'600 CHF</strong>. Économie nette : <strong>42'400 CHF</strong>.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6 flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-[#8A9474]" />
                2. Revenus : Les Pros Génèrent +28% (Données Réelles)
              </h2>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-[#2B2B2B] mb-4">Pourquoi les Conciergeries Performent Mieux</h3>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#2B2B2B] mb-1">Taux de Réponse &lt;1h = +40% Conversions</h4>
                      <p className="text-[#4A4A4A] text-sm">Une conciergerie répond 24/7 en &lt;30min. Vous ? 4-8h en moyenne (travail, nuit). Sur Airbnb, 60% des voyageurs réservent le premier hôte qui répond rapidement.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#2B2B2B] mb-1">Pricing Dynamique Quotidien = +18% ADR</h4>
                      <p className="text-[#4A4A4A] text-sm">Les pros ajustent les prix 2-3× par jour (événements, météo, taux remplissage concurrent). Vous ? Ajustements manuels 1-2× par mois maximum.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#2B2B2B] mb-1">SEO Airbnb : Classement +35% (Algorithme)</h4>
                      <p className="text-[#4A4A4A] text-sm">Taux réponse, annulations, avis 5★ constant : les conciergeries maintiennent des KPIs parfaits = visibilité maximale. Solo : fluctuations = pénalités algorithme.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <CheckCircle2 className="h-6 w-6 text-[#8A9474] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#2B2B2B] mb-1">Occupation +10% (Zéro Trou)</h4>
                      <p className="text-[#4A4A4A] text-sm">Multi-plateforme (Airbnb + Booking + direct), remarketing, early bird : les pros remplissent les creux que vous laissez vides.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 border-2 border-[#8A9474] rounded-lg">
                  <h4 className="font-bold text-[#2B2B2B] mb-2">📊 Exemple Concret (3 Pièces Lausanne)</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-semibold text-[#2B2B2B]">Gestion Solo</p>
                      <ul className="text-[#4A4A4A] space-y-1">
                        <li>• ADR : 220 CHF</li>
                        <li>• Occupation : 75%</li>
                        <li>• CA annuel : <strong>60'225 CHF</strong></li>
                        <li>• Temps : 750h/an</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-[#2B2B2B]">Conciergerie Pro</p>
                      <ul className="text-[#4A4A4A] space-y-1">
                        <li>• ADR : 260 CHF (+18%)</li>
                        <li>• Occupation : 85% (+10%)</li>
                        <li>• CA annuel : <strong>80'665 CHF</strong></li>
                        <li>• Temps : 0h</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-[#2B2B2B] font-bold mt-4">Écart : +20'440 CHF bruts. Commission 22% = 17'746 CHF. <span className="text-[#8A9474]">Gain net : +2'694 CHF + 750h libérées.</span></p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6 flex items-center gap-3">
                <AlertCircle className="h-8 w-8 text-[#8A9474]" />
                3. Erreurs Coûteuses Évitées (Solo vs Pro)
              </h2>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="space-y-4">
                  <Card className="border-l-4 border-red-500">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-[#2B2B2B] mb-2">❌ Erreur #1 : Sous-Tarification (-15-20% CA)</h4>
                      <p className="text-[#4A4A4A] text-sm">Les débutants fixent des prix « attractifs » par peur du vide. Résultat : occupation 90% mais ADR 30% sous le marché = manque à gagner 18K CHF/an.</p>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-red-500">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-[#2B2B2B] mb-2">❌ Erreur #2 : Annulations Non-Gérées (-5-8% CA)</h4>
                      <p className="text-[#4A4A4A] text-sm">Annulation 48h avant : vous ne re-commercialisez pas à temps. Une conciergerie active ses listes d'attente et remplit à 80% ces trous.</p>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-red-500">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-[#2B2B2B] mb-2">❌ Erreur #3 : Litiges Mal Gérés (Coût : 2-5K CHF)</h4>
                      <p className="text-[#4A4A4A] text-sm">Dégât non documenté, guest agressif, plainte voisin : sans process pro, vous perdez les litiges Airbnb (remboursement forcé + suspension compte).</p>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-red-500">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-[#2B2B2B] mb-2">❌ Erreur #4 : Réglementation (Amende : 10-50K CHF)</h4>
                      <p className="text-[#4A4A4A] text-sm">Taxe de séjour oubliée, autorisation manquante, déclaration fiscale erronée : les conciergeries gèrent la conformité légale pour vous.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6 flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-[#8A9474]" />
                4. Grille Tarifaire Conciergeries Suisse 2025
              </h2>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#8A9474]">
                      <th className="text-left py-3 px-2">Formule</th>
                      <th className="text-left py-3 px-2">Commission</th>
                      <th className="text-left py-3 px-2">Services Inclus</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#4A4A4A]">
                    <tr className="border-b">
                      <td className="py-3 px-2 font-semibold">Basique</td>
                      <td className="py-3 px-2">15-18%</td>
                      <td className="py-3 px-2">Annonce + résa + ménages (hors accueil)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-2 font-semibold">Standard</td>
                      <td className="py-3 px-2">20-25%</td>
                      <td className="py-3 px-2">Basique + accueil/check-in + pricing dynamique</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-2 font-semibold">Premium (Homely)</td>
                      <td className="py-3 px-2">22% + 80 CHF/mois</td>
                      <td className="py-3 px-2">Tout inclus : 24/7, photos pro, multi-plateformes, conformité légale</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 font-semibold">Luxe/VIP</td>
                      <td className="py-3 px-2">28-35%</td>
                      <td className="py-3 px-2">Services sur-mesure (conciergerie lifestyle, chef privé, etc.)</td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-6 p-4 bg-[#E8EBE4] rounded-lg">
                  <p className="text-[#2B2B2B] font-semibold mb-2">💡 Attention Frais Cachés</p>
                  <p className="text-sm text-[#4A4A4A]">
                    Certaines conciergeries facturent : frais setup (500-1'500 CHF), photos (300-800 CHF), ménages majorés (+20-30%), maintenance au prix fort. <strong>Homely : prix fixe transparent, aucun frais caché.</strong>
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">❓ FAQ Conciergerie Airbnb</h2>
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#2B2B2B] mb-2">À partir de quel CA déléguer devient rentable ?</h3>
                    <p className="text-[#4A4A4A]">
                      Seuil de rentabilité : <strong>40'000 CHF CA annuel</strong>. En dessous, l'optimisation solo peut suffire. Au-delà, le gain temps + revenus supplémentaires justifie amplement les 20-25% de commission.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#2B2B2B] mb-2">Puis-je garder le contrôle avec une conciergerie ?</h3>
                    <p className="text-[#4A4A4A]">
                      <strong>Oui, totalement</strong>. Vous validez les tarifs planchers, bloquez vos périodes perso, recevez rapports mensuels détaillés et gardez accès complet au calendrier. La conciergerie exécute, vous décidez.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#2B2B2B] mb-2">Que se passe-t-il si je ne suis pas satisfait ?</h3>
                    <p className="text-[#4A4A4A]">
                      Les contrats pros incluent généralement une <strong>clause résiliation 30-60 jours</strong>. Chez Homely : pas d'engagement long terme, vous pouvez arrêter avec préavis 1 mois si les résultats ne suivent pas.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#2B2B2B] mb-2">Ma conciergerie peut-elle gérer plusieurs biens ?</h3>
                    <p className="text-[#4A4A4A]">
                      <strong>Absolument</strong>. C'est même le modèle optimal : économies d'échelle sur ménages, mutualisation équipes, gestion centralisée. Dégressivité tarifaire souvent appliquée (2+ biens : -2-3%)
                    </p>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section className="mt-16">
              <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white overflow-hidden">
                <CardContent className="p-12 text-center">
                  <h2 className="text-3xl font-bold mb-4">
                    Prêt à Déléguer et Maximiser Vos Revenus ?
                  </h2>
                  <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                    Homely Conciergerie : 22% + 80 CHF/mois, tout inclus, sans frais cachés. Revenus optimisés, 0 contrainte.
                  </p>
                  <Button 
                    onClick={() => navigate('/estimation')}
                    size="lg"
                    className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-12 py-7 text-lg font-semibold"
                    data-testid="cta-estimation-conciergerie"
                  >
                    Estimation Gratuite & Sans Engagement
                  </Button>
                  <p className="text-sm text-gray-300 mt-4">
                    Réponse sous 24h • Analyse ROI personnalisée • Tarif transparent
                  </p>
                </CardContent>
              </Card>
            </section>

            <NewsletterCapture />

            <ArticlesConnexes currentSlug="conciergerie-airbnb-pourquoi-deleguer" category="strategie" />

            <div className="mt-12 pt-8 border-t-2 border-gray-200">
              <Button 
                onClick={() => navigate('/blog')}
                variant="outline"
                className="border-[#8A9474] text-[#8A9474] hover:bg-[#8A9474] hover:text-white"
              >
                ← Retour au Blog
              </Button>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  );
}
